import React, { useCallback, useMemo } from "react";
import Select, { GroupTypeBase, NamedProps, OptionTypeBase } from "react-select";
import Creatable from "react-select/creatable";
import { css, cx } from "@emotion/css";
import { getInceptionTheme } from "../../themes/ThemeProvider";
import { IncFaIcon, IncErrorIcon, IncInfoIcon, IncFaIconName } from "../../icons";
import { handleIsValidNewOption } from "../../utils/components/selectUtils";
import { getStringPossibleWidth } from "../../utils";
import { IncToolTip } from "../antd-components";
import { CreateLabelFormatter } from "./common";
import { IncSelectOption2 } from "./types";

export type IncSelectOption<P = any> = IncSelectOption2<P>;

export interface IncGroupSelectOption<P = any> {
  groupLabel: string;
  options: Array<IncSelectOption<P>>;
}

export interface ActionOptionProps {
  /**
   * Text to display for the action option (e.g. "+ Add custom KPI")
   */
  actionText: string;
  /**
   * Callback function to execute when the action option is selected
   */
  onActionClick: () => void;
  /**
   * CSS class to apply to the action option
   */
  actionClassName?: string;
  /**
   * Icon name to display alongside the action text
   */
  actionIconName?: string;
}

export interface ControlAction {
  iconName: string; // The icon to display
  onClick: () => void; // Function to call when clicked
  title?: string; // Optional tooltip text
  className?: string; // Optional CSS class
}

export interface CommonSelectProps {
  /**
   * Label to be shown for the select. Empty string will hide the label.
   */
  label?: string;
  labelClassName?: string;

  helpTextId?: string;
  helpText?: string;

  hasError?: boolean;
  errorText?: string; // pass error text to be shown on tooltip

  allowCreate?: boolean;
  wrapperClass?: string;

  disablePopper?: boolean;
  required?: boolean;
  autoSort?: boolean;
  alignment?: "row" | "column";

  width?: number | string;
  autoAdjustWidth?: boolean;
  autoAdjustWidthFontSize?: number;
  autoAdjustWidthBuffer?: number;
  transparent?: boolean;

  formatCreateLabel?: (input: string) => string | JSX.Element;
  createLabelPrefix?: string;

  /**
   * Props for the custom action option at the bottom of the dropdown
   */
  actionOption?: ActionOptionProps;

  /**
   * Props for the custom control actions
   */
  controlActions?: ControlAction[];
}

export interface IncSelectProps<OptionType extends IncSelectOption, IsMulti extends boolean>
  extends NamedProps<OptionType, IsMulti>,
    CommonSelectProps {
  readOnly?: boolean;
  asTagsEditor?: boolean;
}

interface SelectLabelProps {
  label: string;
  className?: string;
  helpTextId?: string;
  helpText?: string;
  required?: boolean;
}

type SortArg<OptionType extends OptionTypeBase> = OptionType | GroupTypeBase<OptionType>;

export const SelectDropdownIndicator = () => (
  <IncFaIcon
    className="inception-react-select__dropdown-indicator"
    iconName="caret-down"
  />
);

interface ControlWithActionsProps {
  controlActions: ControlAction[];
}

const CustomDropdownIndicator: React.FC<ControlWithActionsProps> = ({ controlActions }) => (
  <div className="inception-react-select__dropdown-container">
    {/* Render custom control actions if provided */}
    {controlActions && controlActions.length > 0 && (
      <div
        className="inception-react-select__control-actions"
        onMouseDown={e => e.stopPropagation()}
      >
        {controlActions.map((action, index) => (
          <div
            className={cx("inception-react-select__control-action", action.className)}
            key={`control-action-${index}`}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              action.onClick();
            }}
            title={action.title || ""}
          >
            <IncFaIcon iconName={action.iconName as IncFaIconName} />
          </div>
        ))}
      </div>
    )}
    {/* Render the original dropdown indicator */}
    <SelectDropdownIndicator />
  </div>
);

export const IncSelectLabel = (props: SelectLabelProps) => {
  const { label, className, helpText, helpTextId, required } = props;
  const appliedClassName = `inc-flex-row inc-text-subtext-medium inception-select-label-container ${className || ""}`;

  return (
    <div className={appliedClassName}>
      <div>{label}</div>
      {required && <span className="inc-span-required-star">*</span>}
      {(helpTextId || helpText) && (
        <IncToolTip
          placement="top-start"
          showArrow
          titleId={helpTextId}
          titleText={helpText}
        >
          <div>
            <IncInfoIcon />
          </div>
        </IncToolTip>
      )}
    </div>
  );
};

const ActionOption = ({ actionProps, ...props }: any) => (
  <div
    className={cx("inc-action-option", actionProps?.actionClassName)}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      if (actionProps?.onActionClick) {
        actionProps.onActionClick();
      }
      props.selectOption({
        value: "__action__",
        label: "",
        __isAction__: true
      });
    }}
  >
    {actionProps?.actionIconName && (
      <IncFaIcon
        className="inc-action-option-icon"
        iconName={actionProps.actionIconName}
      />
    )}
    <span>{actionProps.actionText}</span>
  </div>
);

// Custom Menu component that adds the action option
const MenuWithAction = ({ children, actionProps, ...props }: any) => (
  <div
    className="inception-react-select__menu"
    {...props.innerProps}
  >
    <div className="inception-react-select__menu-list">
      {children}
      {actionProps && (
        <ActionOption
          actionProps={actionProps}
          selectOption={props.selectOption}
        />
      )}
    </div>
  </div>
);

//when there is a type change in props, take care of sorting logic inside compareAndSortOptions function
const IncSelect = <OptionType extends IncSelectOption, IsMulti extends boolean = false>(
  props: IncSelectProps<OptionType, IsMulti>
) => {
  const {
    className: pClassName = "",
    classNamePrefix: pClassNamePrefix = "",
    components: pComponents = {},
    wrapperClass,
    labelClassName,
    disablePopper = false,
    menuPortalTarget: pMenuPortalTarget,
    menuPlacement = "auto",
    errorText,
    hasError,
    required,
    options,
    autoSort = true,
    alignment = "column",
    width,
    formatCreateLabel,
    createLabelPrefix,
    autoAdjustWidth = false,
    autoAdjustWidthBuffer = 0,
    autoAdjustWidthFontSize = 12,
    placeholder,
    value,
    readOnly = false,
    isDisabled = false,
    allowCreate = false,
    label = "",
    helpText = "",
    helpTextId,
    transparent = false,
    styles: defStyles,
    asTagsEditor,
    actionOption,
    controlActions,
    onChange,
    ...restProps
  } = props;

  const theme = getInceptionTheme();

  let valueLabel = Array.isArray(value) ? value.map(v => v.label).join(", ") : (value as OptionType)?.label || "";
  valueLabel = valueLabel || placeholder?.toString() || "";
  const computedWidth = getStringPossibleWidth(valueLabel, autoAdjustWidthFontSize).width + autoAdjustWidthBuffer;

  const className = useMemo(() => {
    const widthStr = typeof width === "number" ? `${width}px` : width;
    const widthClassName =
      autoAdjustWidth && computedWidth
        ? css`
            width: ${computedWidth + (readOnly ? 10 : 36)}px;
          `
        : widthStr
          ? css`
              width: ${widthStr};
            `
          : "";

    return cx("inception-react-select-container", {
      [pClassName]: pClassName !== "",
      [widthClassName]: widthClassName !== ""
    });
  }, [autoAdjustWidth, computedWidth, pClassName, readOnly, width]);

  const wrapperClassName = useMemo(
    () =>
      cx("inception-select", {
        [wrapperClass || ""]: wrapperClass !== "",
        "inc-flex-row": alignment === "row",
        "inc-flex-column": alignment === "column",
        "inception-select--transparent": transparent,
        "tags-editor": asTagsEditor
      }),
    [alignment, asTagsEditor, transparent, wrapperClass]
  );

  const classNamePrefix = pClassNamePrefix ? `${pClassNamePrefix} inception-react-select` : "inception-react-select";

  const components = useMemo(() => {
    const baseComponents = {
      ...pComponents
    };

    // Start with adding custom components
    const customComponents = { ...baseComponents };

    // Replace DropdownIndicator if controlActions are provided
    if (controlActions && controlActions.length > 0) {
      customComponents.DropdownIndicator = (props: any) => (
        <CustomDropdownIndicator
          {...props}
          controlActions={controlActions}
        />
      );
    } else {
      customComponents.DropdownIndicator = pComponents.DropdownIndicator || SelectDropdownIndicator;
    }

    // Add custom Menu component if actionOption is provided
    if (actionOption) {
      customComponents.Menu = (props: any) => (
        <MenuWithAction
          {...props}
          actionProps={actionOption}
        />
      );
    }

    return customComponents;
  }, [pComponents, actionOption, controlActions]);

  const handleChange = useCallback(
    (newValue: any, actionMeta: any) => {
      // If the selected option is our action option, don't update the value
      if (newValue && newValue.__isAction__) {
        return;
      }

      if (onChange) {
        onChange(newValue, actionMeta);
      }
    },
    [onChange]
  );

  const formatCreateLabelValue = useCallback(
    (input: string) =>
      formatCreateLabel ? (
        formatCreateLabel(input)
      ) : (
        <CreateLabelFormatter
          createPrefix={createLabelPrefix}
          input={input}
        />
      ),
    [createLabelPrefix, formatCreateLabel]
  );
  const menuPortalTarget = useMemo(
    () => (pMenuPortalTarget ? pMenuPortalTarget : disablePopper ? null : document.body),
    [disablePopper, pMenuPortalTarget]
  );

  const compareAndSortOptions = useCallback((a: SortArg<OptionType>, b: SortArg<OptionType>) => {
    const labelA = a.label || (a as GroupTypeBase<OptionType>).groupLabel || "";
    const labelB = b.label || (b as GroupTypeBase<OptionType>).groupLabel || "";

    return labelA.localeCompare(labelB);
  }, []);

  const formatGroupLabel = useCallback((group: GroupTypeBase<OptionType>) => {
    const { groupLabel } = group;
    return <div className="inception-select--group-label">{groupLabel}</div>;
  }, []);

  let pOptions = options?.slice();
  pOptions = autoSort ? pOptions?.sort(compareAndSortOptions) : pOptions;

  const errComponent =
    errorText && hasError ? (
      <IncToolTip
        placement="top-start"
        showArrow
        titleText={errorText}
        variant="error"
      >
        <IncErrorIcon
          style={{
            color: theme.inceptionColors.palette.R400,
            marginLeft: !props.label ? 8 : 0
          }}
        />
      </IncToolTip>
    ) : (
      <></>
    );

  const styles = useMemo<IncSelectProps<OptionType, IsMulti>["styles"]>(
    () => ({
      ...(defStyles || {}),
      control: (base: any) => ({
        ...base,
        minHeight: 32
      })
    }),
    [defStyles]
  );

  const dataAttributes = useMemo(
    () =>
      Object.keys(restProps).reduce(
        (acc, key) => {
          if (key.startsWith("data-")) {
            acc[key] = (restProps as Record<string, string>)[key] as string;
          }

          return acc;
        },
        {} as Record<string, string>
      ),
    [restProps]
  );

  return (
    <>
      <div
        className={wrapperClassName}
        data-readonly={readOnly}
        {...dataAttributes}
      >
        {Boolean(label) && (
          <div className="inc-flex-row inc-flex-center-vertical flex-gap-8">
            <IncSelectLabel
              className={labelClassName}
              helpText={helpText}
              helpTextId={helpTextId}
              label={label}
              required={required}
            />
            {errComponent}
          </div>
        )}

        {!allowCreate && (
          <Select<OptionType, IsMulti>
            className={className}
            classNamePrefix={classNamePrefix}
            components={components}
            options={pOptions}
            {...restProps}
            formatGroupLabel={formatGroupLabel}
            isDisabled={readOnly || isDisabled}
            menuPlacement={menuPlacement}
            menuPortalTarget={menuPortalTarget}
            menuShouldScrollIntoView={false}
            onChange={handleChange}
            placeholder={placeholder}
            styles={styles}
            value={value}
          />
        )}

        {allowCreate && (
          <Creatable<OptionType, IsMulti>
            className={className}
            classNamePrefix={classNamePrefix}
            components={components}
            options={pOptions}
            placeholder={placeholder}
            value={value}
            {...restProps}
            allowCreateWhileLoading={true}
            formatCreateLabel={formatCreateLabelValue}
            formatGroupLabel={formatGroupLabel}
            isDisabled={readOnly || isDisabled}
            isValidNewOption={handleIsValidNewOption}
            menuPlacement={menuPlacement}
            menuPortalTarget={menuPortalTarget}
            menuShouldScrollIntoView={false}
            onChange={handleChange}
            styles={styles}
          />
        )}

        {!label && errComponent}
      </div>
    </>
  );
};

export { IncSelect };
